import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { X, Calendar, MapPin, Search, Loader2 } from 'lucide-react';
import { API } from '../utils/apiConfig';

const CATEGORY_LABELS = {
  concert: { de: 'Konzert', emoji: '🎤' },
  sports: { de: 'Sport', emoji: '⚽' },
  festival: { de: 'Festival', emoji: '🎪' },
  fair: { de: 'Messe', emoji: '🏛️' },
  conference: { de: 'Konferenz', emoji: '💼' },
  other: { de: 'Sonstige', emoji: '📍' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso.slice(0, 16);
  }
};

/**
 * EventPickerModal — Phase B
 * Loads upcoming events from /api/events/upcoming and lets the user pick one.
 *
 * Props:
 *   open        — boolean
 *   onClose()
 *   onSelect(event) — receives the full event object (id, name, lat, lng, ...)
 */
export const EventPickerModal = ({ open, onClose, onSelect }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { limit: 50 };
    if (category) params.category = category;

    axios
      .get(`${API}/events/upcoming`, { params })
      .then((r) => {
        if (!cancelled) setEvents(r.data.events || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || 'load_failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, category]);

  if (!open) return null;

  const filtered = events.filter((e) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (e.name || '').toLowerCase().includes(q) ||
      (e.city || '').toLowerCase().includes(q) ||
      (e.venue_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div
      data-testid="event-picker-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)',
        backdropFilter: 'blur(6px)', zIndex: 10010,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        data-testid="event-picker-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 100%)', maxHeight: '85vh',
          background: '#fff', borderRadius: 16, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <header style={{
          padding: '1rem 1.1rem', borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg,#7c3aed 0%,#ec4899 100%)',
          color: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <strong style={{ fontSize: '1rem' }}>
            🎯 {t('events.pickerTitle', 'Event als Ziel wählen')}
          </strong>
          <button
            type="button"
            data-testid="event-picker-close"
            onClick={onClose}
            aria-label="close"
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
              borderRadius: 8, padding: 6, cursor: 'pointer',
              display: 'flex', alignItems: 'center',
            }}
          >
            <X size={18} />
          </button>
        </header>

        {/* Filter bar */}
        <div style={{
          padding: '0.7rem 1.1rem', borderBottom: '1px solid #e2e8f0',
          display: 'flex', gap: 8, flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#94a3b8' }} />
            <input
              type="text"
              data-testid="event-picker-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('events.searchPlaceholder', 'Suche nach Name oder Stadt')}
              style={{
                width: '100%', padding: '8px 10px 8px 30px',
                borderRadius: 8, border: '1px solid #e2e8f0',
                fontSize: 14,
              }}
            />
          </div>
          <select
            data-testid="event-picker-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{
              padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0',
              fontSize: 14, background: '#fff', cursor: 'pointer',
            }}
          >
            <option value="">{t('events.categoryAll', 'Alle Kategorien')}</option>
            {Object.keys(CATEGORY_LABELS).map((k) => (
              <option key={k} value={k}>
                {CATEGORY_LABELS[k].emoji} {t(`events.cat.${k}`, CATEGORY_LABELS[k].de)}
              </option>
            ))}
          </select>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
          {loading && (
            <div data-testid="event-picker-loading" style={{
              padding: '2rem', display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 8, color: '#64748b',
            }}>
              <Loader2 size={28} className="spinning" />
              <span>{t('events.loading', 'Events werden geladen…')}</span>
            </div>
          )}

          {error && !loading && (
            <div data-testid="event-picker-error" style={{
              padding: '1.5rem', textAlign: 'center', color: '#ef4444',
            }}>
              {t('events.loadFailed', 'Events konnten nicht geladen werden.')}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div data-testid="event-picker-empty" style={{
              padding: '1.5rem', textAlign: 'center', color: '#64748b', fontSize: 14,
            }}>
              {t('events.empty', 'Keine passenden Events gefunden.')}
            </div>
          )}

          {!loading && !error && filtered.map((ev) => {
            const meta = CATEGORY_LABELS[ev.category] || CATEGORY_LABELS.other;
            return (
              <button
                type="button"
                key={ev.id}
                data-testid={`event-row-${ev.id}`}
                onClick={() => onSelect(ev)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', textAlign: 'left',
                  padding: '0.7rem 0.9rem', margin: '4px 0',
                  background: '#fff', border: '1px solid #e2e8f0',
                  borderRadius: 10, cursor: 'pointer',
                  transition: 'all 120ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#7c3aed';
                  e.currentTarget.style.background = '#faf5ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.background = '#fff';
                }}
              >
                <span style={{
                  fontSize: 22, width: 36, height: 36, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(124,58,237,0.10)', borderRadius: 8,
                }}>
                  {meta.emoji}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {ev.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 2 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Calendar size={11} /> {formatDate(ev.start_at)}
                    </span>
                    {ev.city && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <MapPin size={11} /> {ev.city}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EventPickerModal;
